import {FC, useCallback, useContext, useMemo} from 'react';
import {Table, Form, SemanticWIDTHS} from 'semantic-ui-react';
import {map, mapValues, get, size, omit, filter, isEmpty, compact, castArray} from 'lodash';
import {Field, ListInput, ValueInput, ValueInputRendererProps, ValueRenderer} from 'apstra-ui-common';

import IBAContext from '../IBAContext';

import './EnvironmentExpectationsInput.less';

const columns = {
  device_profile_label: {title: 'Device Profile Label', width: 7},
  power_supply_count: {title: 'Power Supply Count', width: 4},
  fan_tray_count: {title: 'Fan Tray Count', width: 4},
};
const generateNewEntry = () => mapValues(columns, () => '');
const isItemRemovable = (item) => item.device_profile_label !== 'default';

const EnvironmentExpectationsInput: FC<ValueInputRendererProps> = ({
  name, value = [], schema, required, disabled, errors, onChange, fieldProps
}) => {
  const {deviceProfiles} = useContext(IBAContext);
  const onFieldChange = useCallback((index, field, newRowInputValue) => {
    const newInputValue = [...value];
    newInputValue[index][field] = newRowInputValue;
    onChange(newInputValue);
  }, [value, onChange]);

  const errorsByItems = useMemo(() => ListInput.getErrorByItems(errors), [errors]);
  const propertiesSchema = get(schema, ['items', 'properties'], {});
  const deviceProfileLabelDropdownOptions = useMemo(() => {
    return map(
      filter(deviceProfiles, ({os}) => os === 'Junos'),
      ({label}) => ({const: label, title: label})
    );
  }, [deviceProfiles]);

  return (
    <ListInput
      name={name}
      value={value}
      schema={schema}
      required={required}
      disabled={disabled}
      errors={errorsByItems.generic}
      onChange={onChange}
      generateNewEntry={generateNewEntry}
      isItemRemovable={isItemRemovable}
      minItems={1}
      buttonText='Add Environment Expectations'
      header={<EnvironmentExpectationsInputHeader />}
      fieldProps={fieldProps}
    >
      {({value, disabled, index}) => map(columns, ({width}, key) => (
        <ValueInput
          key={key}
          value={value[key]}
          schema={
            key === 'device_profile_label' ?
              {
                oneOf: value.device_profile_label === 'default' ?
                  [{const: 'default', title: 'default'}]
                :
                  deviceProfileLabelDropdownOptions
              }
            :
              omit(propertiesSchema[key], ['title', 'description'])
          }
          disabled={
            disabled ||
            key === 'device_profile_label' && (
              value.device_profile_label === 'default' ||
              !deviceProfileLabelDropdownOptions.length
            )
          }
          placeholder={
            key === 'device_profile_label' && !deviceProfileLabelDropdownOptions.length ?
              'No available device profiles' : undefined
          }
          fieldProps={{width: width as SemanticWIDTHS}}
          errors={compact(castArray(errorsByItems.byIndex[index]?.[key]))}
          onChange={(rowInputValue) => onFieldChange(index, key, rowInputValue)}
        />
      ))}
    </ListInput>
  );
};

export default EnvironmentExpectationsInput;

const EnvironmentExpectationsInputHeader = () => (
  <Field className='field-subheader'>
    <Form.Group>
      {map(columns, ({title, width}, key) =>
        <Field key={key} width={width as SemanticWIDTHS} label={title} />
      )}
    </Form.Group>
  </Field>
);

export const EnvironmentExpectations = ({value}) => (
  <Table basic='very' collapsing size='small' className='environment-expectations'>
    <Table.Header>
      <Table.Row>
        {map(columns, ({title, width}, key) =>
          <Table.HeaderCell key={key} width={width as SemanticWIDTHS}>{title}</Table.HeaderCell>
        )}
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {isEmpty(value) ?
        <Table.Row>
          <Table.Cell colSpan={size(columns)}>{'No items.'}</Table.Cell>
        </Table.Row>
      :
        map(value, (row, index) => (
          <Table.Row key={index}>
            {map(columns, (_, key) =>
              <Table.Cell key={key}>{row[key]}</Table.Cell>
            )}
          </Table.Row>
        ))
      }
    </Table.Body>
  </Table>
);

export const environmentExpectationsRenderer = new ValueRenderer({
  condition: ({name, schema}) => name === 'expectation' && schema?.type === 'array',
  Value: EnvironmentExpectations,
  ValueInput: EnvironmentExpectationsInput,
});
